import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';
import { useTenant } from '../contexts/TenantContext';
import { useT } from '../i18n';

/**
 * Análise das conversas com IA: a IA lê cada conversa do período (pedido, itens, região, de onde veio,
 * reclamação, por que não pediu), a plataforma soma os números e a IA escreve o relatório.
 */

type Status = 'RUNNING' | 'SUMMARIZING' | 'DONE' | 'FAILED' | 'CANCELLED';

interface Result {
  intent: string; ordered: boolean; items: string[]; orderValue: number; orderType: string; deliveryArea: string;
  sourceMentioned: string; sourceDetail: string; sentiment: string; complaint: string; lostReason: string; summary: string;
}

interface Item {
  id: string; phone: string; contactName: string | null; sessionName: string | null; sourceType: string | null; sourceTitle: string | null;
  returning: boolean; inboundCount: number; outboundCount: number; firstInboundAt: string | null; firstReplyMinutes: number | null;
  status: string; result: Result | null; error: string | null;
}

interface Totals {
  conversations: number; analysed: number; failed: number; ordered: number; conversion: number; revenue: number; averageOrder: number | null;
  ordersWithValue: number; newCustomers: { conversations: number; ordered: number }; returningCustomers: { conversations: number; ordered: number };
  unanswered: number; medianReplyMinutes: number | null; slowReplies: number; adChats: number;
  intents: Record<string, number>; orderTypes: Record<string, number>; sources: Record<string, number>; sentiments: Record<string, number>;
  topItems: { label: string; count: number }[]; askedNotOrdered: { label: string; count: number }[]; topAreas: { label: string; count: number }[];
  complaints: { phone: string; name: string | null; text: string }[]; lostReasons: { phone: string; name: string | null; text: string }[];
  byNumber: { number: string; conversations: number; ordered: number; conversion: number; revenue: number; unanswered: number; medianReplyMinutes: number | null }[];
  peakHours: number[]; weekdays: number[];
}

interface Analysis {
  id: string; name: string; status: Status; periodFrom: string; periodTo: string; aiProvider: string; aiModel: string;
  createdAt: string; completedAt: string | null; error: string | null; report?: string | null; totals?: Totals | null;
  counts?: Record<string, number>; items?: Item[];
}

const STATUS_STYLE: Record<string, string> = {
  RUNNING: 'bg-purple-100 text-purple-800', SUMMARIZING: 'bg-purple-100 text-purple-800', DONE: 'bg-emerald-100 text-emerald-800',
  FAILED: 'bg-red-100 text-red-700', CANCELLED: 'bg-gray-100 text-gray-600',
};

function toDateInput(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Mini-markdown do relatório: "## título" e "- item" */
function ReportText({ text }: { text: string }) {
  return (
    <div dir="auto" className="space-y-1 text-sm leading-7 text-gray-800">
      {text.split('\n').map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        if (trimmed.startsWith('## ')) return <h3 key={i} className="text-base font-semibold text-gray-900 pt-3">{trimmed.slice(3)}</h3>;
        if (trimmed.startsWith('- ')) return <p key={i} className="ps-4 relative before:content-['•'] before:absolute before:start-0">{trimmed.slice(2)}</p>;
        return <p key={i}>{trimmed}</p>;
      })}
    </div>
  );
}

export function ChatAnalysisPage() {
  const t = useT();
  const { selectedTenantId } = useTenant();
  const [list, setList] = useState<Analysis[]>([]);
  const [current, setCurrent] = useState<Analysis | null>(null);
  const [sessions, setSessions] = useState<{ name: string; displayName?: string }[]>([]);
  const [models, setModels] = useState<Record<string, string[]>>({ CLAUDE: ['claude-opus-5-5'], OPENAI: ['gpt-4o-mini'], GROQ: ['llama-3.3-70b-versatile'] });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [filter, setFilter] = useState<'ALL' | 'ORDERED' | 'NOT_ORDERED' | 'COMPLAINT' | 'UNANSWERED'>('ALL');
  const [form, setForm] = useState({
    name: '', from: toDateInput(new Date(Date.now() - 7 * 86400000)), to: toDateInput(new Date()),
    sessionNames: [] as string[], aiProvider: 'CLAUDE', aiModel: 'claude-opus-5-5',
  });

  const api = useCallback(async (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` };
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(url, { ...options, headers });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    return data;
  }, [selectedTenantId]);

  const loadList = useCallback(() => api('/api/chat-analyses').then(setList).catch(e => toast.error(e.message)), [api]);
  const open = useCallback((id: string) => api(`/api/chat-analyses/${id}`).then(setCurrent).catch(e => toast.error(e.message)), [api]);

  useEffect(() => {
    loadList();
    api('/api/waha/sessions').then((data: any[]) => setSessions(data.map(s => ({ name: s.name, displayName: s.displayName })))).catch(() => undefined);
    api('/api/chat-analyses/options').then(data => setModels(data.models)).catch(() => undefined);
  }, [api, loadList]);

  useEffect(() => {
    if (!current || !['RUNNING', 'SUMMARIZING'].includes(current.status)) return;
    const timer = setInterval(() => open(current.id), 5000);
    return () => clearInterval(timer);
  }, [current, open]);

  const period = () => ({
    periodFrom: new Date(`${form.from}T00:00:00`).toISOString(),
    periodTo: new Date(`${form.to}T23:59:59.999`).toISOString(),
  });

  // Mudou o período ou os números: a contagem anterior não vale mais
  useEffect(() => { setPreview(null); }, [form.from, form.to, form.sessionNames]);

  const run = async (fn: () => Promise<any>, success?: string) => {
    setBusy(true);
    try {
      await fn();
      if (success) toast.success(success);
    } catch (error: any) {
      toast.error(error.message, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const countChats = () => run(async () => {
    const data = await api('/api/chat-analyses/preview', { method: 'POST', body: JSON.stringify({ ...period(), sessionNames: form.sessionNames }) });
    setPreview(data.conversations);
  });

  const start = () => run(async () => {
    const created = await api('/api/chat-analyses', { method: 'POST', body: JSON.stringify({ ...form, ...period() }) });
    setShowForm(false);
    await open(created.id);
    loadList();
  }, t('ca.started'));

  const action = (path: string, success: string, method = 'POST') => current && run(async () => {
    await api(`/api/chat-analyses/${current.id}${path}`, { method });
    if (method === 'DELETE') setCurrent(null); else await open(current.id);
    loadList();
  }, success);

  const items = current?.items || [];
  const totals = current?.totals || null;
  const shown = useMemo(() => items.filter(i => {
    const r = i.result;
    if (filter === 'ORDERED') return r?.ordered;
    if (filter === 'NOT_ORDERED') return r && !r.ordered;
    if (filter === 'COMPLAINT') return Boolean(r?.complaint);
    if (filter === 'UNANSWERED') return i.outboundCount === 0;
    return true;
  }), [items, filter]);

  const exportCsv = () => {
    const quote = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['name', 'phone', 'number', 'intent', 'ordered', 'items', 'value', 'type', 'area', 'source', 'sentiment', 'complaint', 'lost_reason', 'reply_minutes', 'summary'];
    const rows = shown.map(i => [i.contactName, `+${i.phone}`, i.sessionName, i.result && t(`ca.intent.${i.result.intent}`), i.result?.ordered ? 'yes' : 'no',
      i.result?.items.join(' | '), i.result?.orderValue || '', i.result?.orderType, i.result?.deliveryArea, i.result && t(`ca.source.${i.result.sourceMentioned}`),
      i.result?.sentiment, i.result?.complaint, i.result?.lostReason, i.firstReplyMinutes ?? '', i.result?.summary].map(quote).join(','));
    const blob = new Blob(['﻿' + [header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `chat-analysis-${current?.periodFrom.slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const minutes = (m: number | null) => (m === null ? '—' : m < 60 ? t('ca.min', { n: m }) : t('ca.hours', { n: Math.round(m / 6) / 10 }));
  const countTable = (title: string, data: Record<string, number>, prefix: string) => {
    const entries = Object.entries(data).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
        {entries.length === 0 ? <p className="text-sm text-gray-400">—</p> : (
          <table className="w-full text-sm"><tbody className="divide-y">
            {entries.map(([key, n]) => (
              <tr key={key}><td className="py-1.5">{t(`${prefix}.${key}`)}</td><td className="py-1.5 text-end tabular-nums font-medium">{n}</td></tr>
            ))}
          </tbody></table>
        )}
      </div>
    );
  };
  const labelTable = (title: string, rows: { label: string; count: number }[]) => (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-gray-400">—</p> : (
        <table className="w-full text-sm"><tbody className="divide-y">
          {rows.map(row => <tr key={row.label}><td className="py-1.5" dir="auto">{row.label}</td><td className="py-1.5 text-end tabular-nums font-medium">{row.count}</td></tr>)}
        </tbody></table>
      )}
    </div>
  );
  const textList = (title: string, rows: { phone: string; name: string | null; text: string }[]) => (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="font-semibold text-gray-900 mb-2">{title} <span className="text-gray-400 font-normal">({rows.length})</span></h3>
      {rows.length === 0 ? <p className="text-sm text-gray-400">—</p> : (
        <ul className="space-y-1.5 text-sm max-h-80 overflow-y-auto">
          {rows.map((row, i) => (
            <li key={i} dir="auto"><span className="text-gray-800">{row.text}</span> <span className="text-xs text-gray-400" dir="ltr">· {row.name || `+${row.phone}`}</span></li>
          ))}
        </ul>
      )}
    </div>
  );

  const formModal = showForm && (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 space-y-4">
          <h2 className="text-xl font-bold text-gray-900">{t('ca.newTitle')}</h2>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('ca.name')}</span>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('ca.namePlaceholder')}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" />
          </label>
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('ca.period')}</span>
            <div className="flex flex-wrap gap-2 mt-1 mb-2">
              {[1, 7, 14, 30].map(days => (
                <button key={days} type="button" className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => setForm({ ...form, from: toDateInput(new Date(Date.now() - (days - 1) * 86400000)), to: toDateInput(new Date()) })}>
                  {t('ca.lastDays', { n: days })}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-md" />
              <input type="date" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-md" />
            </div>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('ca.numbers')}</span>
            <p className="text-xs text-gray-500">{t('ca.numbersHelp')}</p>
            <div className="mt-1 space-y-1">
              {sessions.map(s => (
                <label key={s.name} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.sessionNames.includes(s.name)}
                    onChange={e => setForm({ ...form, sessionNames: e.target.checked ? [...form.sessionNames, s.name] : form.sessionNames.filter(n => n !== s.name) })} />
                  {s.displayName || s.name}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('fu.aiProvider')}</span>
              <select value={form.aiProvider} onChange={e => setForm({ ...form, aiProvider: e.target.value, aiModel: models[e.target.value]?.[0] || '' })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="CLAUDE">Claude (Anthropic)</option>
                <option value="OPENAI">OpenAI</option>
                <option value="GROQ">Groq</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('fu.aiModel')}</span>
              <select value={form.aiModel} onChange={e => setForm({ ...form, aiModel: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                {(models[form.aiProvider] || []).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
          </div>
          <p className="text-xs text-gray-500">{t('ca.costNote')}</p>
          <div className="flex items-center gap-2">
            <button type="button" disabled={busy} onClick={countChats} className="px-3 py-2 text-sm bg-gray-100 rounded-md">{t('ca.count')}</button>
            {preview !== null && <span className="text-sm text-blue-800">{t('ca.willAnalyse', { n: preview })}</span>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-gray-100 rounded-md">{t('common.cancel')}</button>
            <button type="button" disabled={busy} onClick={start} className="px-4 py-2 bg-purple-600 text-white rounded-md disabled:opacity-50">✨ {t('ca.start')}</button>
          </div>
        </div>
      </div>
    </Portal>
  );

  if (!current) {
    return (
      <div>
        <Header title={t('ca.title')} subtitle={t('ca.subtitle')}
          actions={<button onClick={() => { setPreview(null); setShowForm(true); }} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">✨ {t('ca.new')}</button>} />
        <div className="p-6">
          {list.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">{t('ca.empty')}</div>
          ) : (
            <div className="bg-white rounded-lg shadow divide-y">
              {list.map(a => (
                <button key={a.id} onClick={() => open(a.id)} className="w-full text-start p-4 hover:bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{a.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[a.status]}`}>{t(`ca.status.${a.status}`)}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{date(a.periodFrom)} → {date(a.periodTo)} · {a.aiModel}</div>
                  </div>
                  <span className="text-sm text-gray-600">{t('ca.chats', { n: Object.values(a.counts || {}).reduce((x, y) => x + y, 0) })}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {formModal}
      </div>
    );
  }

  const done = items.filter(i => i.status === 'DONE').length;
  const failed = items.filter(i => i.status === 'FAILED').length;
  const weekdayNames = [0, 1, 2, 3, 4, 5, 6].map(d => new Date(2026, 9, 4 + d).toLocaleDateString(undefined, { weekday: 'short' }));

  return (
    <div>
      <Header title={current.name} subtitle={`${date(current.periodFrom)} → ${date(current.periodTo)} · ${current.aiModel}`}
        actions={<button onClick={() => { setCurrent(null); loadList(); }} className="px-4 py-2 bg-gray-100 rounded-lg text-sm">{t('ca.back')}</button>} />

      <div className="p-6 space-y-6">
        <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[current.status]}`}>{t(`ca.status.${current.status}`)}</span>
            <span>{t('ca.progress', { done, total: items.length, failed })}</span>
          </div>
          <div className="flex gap-2">
            {current.status === 'RUNNING' && <button disabled={busy} onClick={() => action('/cancel', t('ca.stopped'))} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded">{t('ca.stop')}</button>}
            {['DONE', 'FAILED'].includes(current.status) && (
              <button disabled={busy} onClick={() => action('/retry', t('ca.started'))} className="px-3 py-1.5 text-sm bg-purple-100 text-purple-800 rounded">
                {failed > 0 ? t('ca.retryFailed', { n: failed }) : t('ca.rewriteReport')}
              </button>
            )}
            {!['RUNNING', 'SUMMARIZING'].includes(current.status) && (
              <button disabled={busy} onClick={() => confirm(t('ca.confirmDelete')) && action('', t('ca.deleted'), 'DELETE')} className="px-3 py-1.5 text-sm bg-gray-100 rounded">{t('ca.delete')}</button>
            )}
            {items.length > 0 && <button onClick={exportCsv} className="px-3 py-1.5 text-sm bg-gray-100 rounded">{t('ci.export')}</button>}
          </div>
        </div>
        {current.error && <p className="text-sm text-red-600">{current.error}</p>}

        {current.report && (
          <section className="bg-white rounded-lg shadow p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">✨ {t('ca.report')}</h2>
            <ReportText text={current.report} />
          </section>
        )}

        {totals && (
          <>
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                [t('ca.kpi.chats'), totals.conversations],
                [t('ca.kpi.orders'), `${totals.ordered} (${totals.conversion}%)`],
                [t('ca.kpi.revenue'), totals.revenue ? `${totals.revenue} AED` : '—', totals.averageOrder ? t('ca.kpi.avg', { n: totals.averageOrder, k: totals.ordersWithValue }) : ''],
                [t('ca.kpi.reply'), minutes(totals.medianReplyMinutes), t('ca.kpi.slow', { n: totals.slowReplies })],
                [t('ca.kpi.unanswered'), totals.unanswered],
                [t('ca.kpi.new'), `${totals.newCustomers.conversations} → ${totals.newCustomers.ordered}`, t('ca.kpi.chatsToOrders')],
                [t('ca.kpi.returning'), `${totals.returningCustomers.conversations} → ${totals.returningCustomers.ordered}`, t('ca.kpi.chatsToOrders')],
                [t('ca.kpi.ads'), totals.adChats],
              ].map(([label, value, hint], i) => (
                <div key={i} className="bg-white rounded-lg shadow p-4">
                  <div className="text-xs text-gray-500">{label}</div>
                  <div className="text-xl font-bold tabular-nums mt-1">{value}</div>
                  {hint && <div className="text-xs text-gray-400 mt-0.5">{hint}</div>}
                </div>
              ))}
            </section>

            <section className="bg-white rounded-lg shadow p-4 overflow-x-auto">
              <h3 className="font-semibold text-gray-900 mb-2">{t('ca.byNumber')}</h3>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500"><tr>
                  <th className="text-start py-1">{t('ca.col.number')}</th><th className="text-end">{t('ca.kpi.chats')}</th><th className="text-end">{t('ca.kpi.orders')}</th>
                  <th className="text-end">%</th><th className="text-end">{t('ca.kpi.revenue')}</th><th className="text-end">{t('ca.kpi.unanswered')}</th><th className="text-end">{t('ca.kpi.reply')}</th>
                </tr></thead>
                <tbody className="divide-y">
                  {totals.byNumber.map(n => (
                    <tr key={n.number}>
                      <td className="py-1.5">{n.number}</td><td className="text-end tabular-nums">{n.conversations}</td><td className="text-end tabular-nums">{n.ordered}</td>
                      <td className="text-end tabular-nums">{n.conversion}%</td><td className="text-end tabular-nums">{n.revenue || '—'}</td>
                      <td className="text-end tabular-nums">{n.unanswered}</td><td className="text-end tabular-nums">{minutes(n.medianReplyMinutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {countTable(t('ca.intents'), totals.intents, 'ca.intent')}
              {countTable(t('ca.sources'), totals.sources, 'ca.source')}
              {countTable(t('ca.orderTypes'), totals.orderTypes, 'ca.orderType')}
              {labelTable(t('ca.topItems'), totals.topItems)}
              {labelTable(t('ca.askedNotOrdered'), totals.askedNotOrdered)}
              {labelTable(t('ca.areas'), totals.topAreas)}
            </section>

            <section className="grid md:grid-cols-2 gap-4">
              {textList(t('ca.complaints'), totals.complaints)}
              {textList(t('ca.lostReasons'), totals.lostReasons)}
            </section>

            <section className="bg-white rounded-lg shadow p-4 overflow-x-auto">
              <h3 className="font-semibold text-gray-900 mb-2">{t('ca.peak')}</h3>
              <table className="text-xs">
                <tbody>
                  <tr>{totals.peakHours.map((_, h) => <th key={h} className="px-1.5 py-1 font-normal text-gray-500">{h}</th>)}</tr>
                  <tr>{totals.peakHours.map((n, h) => <td key={h} className={`px-1.5 py-1 text-center tabular-nums ${n === Math.max(...totals.peakHours) && n > 0 ? 'font-bold text-purple-700' : ''}`}>{n}</td>)}</tr>
                </tbody>
              </table>
              <table className="text-sm mt-3">
                <tbody>
                  <tr>{weekdayNames.map(d => <th key={d} className="px-3 py-1 font-normal text-gray-500">{d}</th>)}</tr>
                  <tr>{totals.weekdays.map((n, d) => <td key={d} className={`px-3 py-1 text-center tabular-nums ${n === Math.max(...totals.weekdays) && n > 0 ? 'font-bold text-purple-700' : ''}`}>{n}</td>)}</tr>
                </tbody>
              </table>
            </section>
          </>
        )}

        <section className="bg-white rounded-lg shadow">
          <div className="p-3 flex flex-wrap gap-2 border-b">
            {(['ALL', 'ORDERED', 'NOT_ORDERED', 'COMPLAINT', 'UNANSWERED'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1 text-xs rounded-full border ${filter === f ? 'bg-blue-50 border-blue-400 text-blue-800' : 'border-gray-300'}`}>{t(`ca.filter.${f}`)}</button>
            ))}
            <span className="text-sm text-gray-500 ms-auto">{t('ca.chats', { n: shown.length })}</span>
          </div>
          <div className="divide-y">
            {shown.map(i => (
              <div key={i.id} className="p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div>
                    <span className="font-medium" dir="auto">{i.contactName || `+${i.phone}`}</span>
                    <span className="text-xs text-gray-500 ms-2" dir="ltr">+{i.phone}</span>
                    <span className="text-xs text-gray-400 ms-2">{i.sessionName}{i.returning ? ` · ${t('ca.returning')}` : ''}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 text-xs">
                    {i.result && <span className="px-2 py-0.5 rounded-full bg-gray-100">{t(`ca.intent.${i.result.intent}`)}</span>}
                    {i.result?.ordered && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{t('ca.orderedTag')}{i.result.orderValue ? ` · ${i.result.orderValue} AED` : ''}</span>}
                    {i.result && i.result.sourceMentioned !== 'NONE' && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-800">{t(`ca.source.${i.result.sourceMentioned}`)}</span>}
                    {i.outboundCount === 0 && <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700">{t('ca.filter.UNANSWERED')}</span>}
                    {i.status !== 'DONE' && <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{t(`ca.itemStatus.${i.status}`)}</span>}
                  </div>
                </div>
                {i.result?.summary && <p dir="auto" className="text-gray-700 mt-1">{i.result.summary}</p>}
                {i.result && (i.result.items.length > 0 || i.result.deliveryArea) && (
                  <p dir="auto" className="text-xs text-gray-500 mt-0.5">{[i.result.items.join('، '), i.result.deliveryArea].filter(Boolean).join(' · ')}</p>
                )}
                {i.result?.complaint && <p dir="auto" className="text-xs text-red-700 mt-0.5">⚠ {i.result.complaint}</p>}
                {i.result?.lostReason && !i.result.ordered && <p dir="auto" className="text-xs text-amber-700 mt-0.5">↯ {i.result.lostReason}</p>}
                {i.error && <p className="text-xs text-red-600 mt-0.5">{i.error}</p>}
                <p className="text-xs text-gray-400 mt-0.5">{t('ca.replyIn', { t: minutes(i.firstReplyMinutes) })}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
